import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';

const formatSize = (bytes) => (bytes >= 1024 * 1024 * 1024
  ? `${Math.round((bytes / 1024 / 1024 / 1024) * 10) / 10} GB`
  : `${Math.round(bytes / 1024 / 1024)} MB`);

// Public intake form at /f/:slug. No login, no auth header — anyone with the
// link can submit, and a submission becomes a card on the configured board.
const PublicForm = () => {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(null); // { name, percent, index, total }
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`/api/forms/public/${slug}`);
        if (!cancelled) setForm(res.data);
      } catch (err) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const setAnswer = useCallback((key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  }, []);

  const toggleMulti = (key, option) => {
    setAnswers(prev => {
      const current = Array.isArray(prev[key]) ? prev[key] : [];
      return {
        ...prev,
        [key]: current.includes(option) ? current.filter(v => v !== option) : [...current, option]
      };
    });
  };

  // Big artwork goes browser → S3 directly on a presigned PUT, so a 1 GB file
  // never travels through nginx or the app server.
  const uploadDirect = async (fieldKey, file, onProgress) => {
    const { data } = await axios.post(`/api/forms/public/${slug}/upload-url`, {
      fieldKey,
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      size: file.size
    });

    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', data.uploadUrl);
      xhr.setRequestHeader('Content-Type', data.contentType);
      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) onProgress(Math.round((evt.loaded / evt.total) * 100));
      };
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`)));
      xhr.onerror = () => reject(new Error('Upload failed. Check your connection and try again.'));
      xhr.send(file);
    });

    return { key: data.key, name: file.name, size: file.size };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Client-side required check so people don't lose a long form to a 400.
    const missing = form.fields.find(f => f.required && (
      f.type === 'file'
        ? !(files[f.key] || []).length
        : !(Array.isArray(answers[f.key]) ? answers[f.key].length : String(answers[f.key] ?? '').trim())
    ));
    if (missing) return setError(`${missing.label} is required`);

    const maxBytes = form.maxFileBytes || 25 * 1024 * 1024;
    const oversize = Object.values(files).flat().find(f => f.size > maxBytes);
    if (oversize) return setError(`"${oversize.name}" is larger than ${formatSize(maxBytes)}. Send a link to it instead.`);

    setSubmitting(true);
    try {
      const chosen = Object.entries(files).flatMap(([key, list]) => list.map(file => ({ key, file })));

      if (form.directUpload) {
        // Upload everything first, then submit a small JSON payload of keys.
        const uploads = {};
        for (let i = 0; i < chosen.length; i++) {
          const { key, file } = chosen[i];
          setProgress({ name: file.name, percent: 0, index: i + 1, total: chosen.length });
          const uploaded = await uploadDirect(key, file, (percent) =>
            setProgress({ name: file.name, percent, index: i + 1, total: chosen.length }));
          (uploads[key] = uploads[key] || []).push(uploaded);
        }
        setProgress(null);
        const res = await axios.post(`/api/forms/public/${slug}/submit`, {
          answers,
          uploads,
          _website: honeypot
        });
        setDone(res.data);
      } else {
        const payload = new FormData();
        payload.append('answers', JSON.stringify(answers));
        payload.append('_website', honeypot);
        for (const { key, file } of chosen) payload.append(`file_${key}`, file);
        const res = await axios.post(`/api/forms/public/${slug}/submit`, payload);
        setDone(res.data);
      }
    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Something went wrong. Try again in a moment.');
    } finally {
      setProgress(null);
      setSubmitting(false);
    }
  };

  const inputClass = 'w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent';

  const renderField = (field) => {
    const value = answers[field.key] ?? '';
    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            rows={5}
            className={inputClass}
            placeholder={field.placeholder}
            value={value}
            onChange={e => setAnswer(field.key, e.target.value)}
          />
        );
      case 'select':
        return (
          <select className={inputClass} value={value} onChange={e => setAnswer(field.key, e.target.value)}>
            <option value="">Choose one…</option>
            {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        );
      case 'multiselect':
        return (
          <div className="space-y-2">
            {field.options.map(opt => (
              <label key={opt} className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary-blue focus:ring-primary-blue"
                  checked={Array.isArray(value) && value.includes(opt)}
                  onChange={() => toggleMulti(field.key, opt)}
                />
                {opt}
              </label>
            ))}
          </div>
        );
      case 'checkbox':
        return (
          <label className="flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary-blue focus:ring-primary-blue"
              checked={value === true}
              onChange={e => setAnswer(field.key, e.target.checked)}
            />
            {field.placeholder || 'Yes'}
          </label>
        );
      case 'file': {
        const chosen = files[field.key] || [];
        return (
          <div>
            <input
              type="file"
              multiple={field.maxFiles > 1}
              onChange={e => setFiles(prev => ({ ...prev, [field.key]: Array.from(e.target.files).slice(0, field.maxFiles) }))}
              className="block w-full text-sm text-gray-600 dark:text-gray-400 file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-bold file:bg-primary-blue file:text-white hover:file:opacity-90"
            />
            {!!chosen.length && (
              <ul className="mt-2 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                {chosen.map(f => (
                  <li key={f.name}>{f.name} — {(f.size / 1024 / 1024).toFixed(1)} MB</li>
                ))}
              </ul>
            )}
            <p className="mt-1 text-xs text-gray-400">
              Up to {field.maxFiles} file{field.maxFiles > 1 ? 's' : ''}, {formatSize(form.maxFileBytes || 25 * 1024 * 1024)} each.
            </p>
          </div>
        );
      }
      default:
        return (
          <input
            type={field.type === 'email' ? 'email' : field.type === 'date' ? 'date' : field.type === 'phone' ? 'tel' : 'text'}
            className={inputClass}
            placeholder={field.placeholder}
            value={value}
            onChange={e => setAnswer(field.key, e.target.value)}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-blue" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 px-4">
        <div className="text-center">
          <h1 className="text-2xl font-black text-gray-900 dark:text-gray-100">Form not found</h1>
          <p className="mt-2 text-gray-500">This form doesn't exist or has been turned off. Check the link you were sent.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-xl ring-1 ring-black/5 dark:ring-white/10 p-8 sm:p-10">
          <h1 className="text-3xl font-black tracking-tight text-gray-900 dark:text-gray-100">{form.name}</h1>
          {form.intro && (
            <p className="mt-3 text-gray-600 dark:text-gray-400 whitespace-pre-line">{form.intro}</p>
          )}

          {done ? (
            <div className="mt-8 rounded-2xl bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 p-6 text-center">
              <p className="text-green-800 dark:text-green-200 font-bold">{done.message || 'Thanks! Your request has been submitted.'}</p>
              {done.updatePath && (
                <div className="mt-4 text-sm text-green-900 dark:text-green-100">
                  <p>Need to add a comment or more files later? Use your private link:</p>
                  <Link to={done.updatePath} className="mt-2 inline-block font-bold text-primary-blue hover:underline break-all">
                    {window.location.origin}{done.updatePath}
                  </Link>
                  <p className="mt-1 text-xs text-green-700 dark:text-green-300">
                    {done.emailed ? "We also emailed it to you." : 'Bookmark it, it won\'t be shown again.'}
                  </p>
                </div>
              )}
              <button
                onClick={() => { setDone(null); setAnswers({}); setFiles({}); }}
                className="mt-4 text-sm font-bold text-primary-blue hover:underline"
              >
                Submit another
              </button>
            </div>
          ) : (
            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              {form.fields.map(field => (
                <div key={field.key}>
                  <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-2">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {renderField(field)}
                  {field.help && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{field.help}</p>}
                </div>
              ))}

              {/* Honeypot — hidden from people, irresistible to bots. */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={e => setHoneypot(e.target.value)}
                className="absolute left-[-9999px] w-px h-px opacity-0"
                aria-hidden="true"
              />

              {error && (
                <div className="rounded-xl bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-200">
                  {error}
                </div>
              )}

              {progress && (
                <div>
                  <div className="flex justify-between text-xs font-bold text-gray-600 dark:text-gray-300 mb-1">
                    <span className="truncate pr-3">Uploading {progress.name}</span>
                    <span>{progress.index} of {progress.total} · {progress.percent}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                    <div className="h-full bg-primary-blue transition-[width] duration-200" style={{ width: `${progress.percent}%` }} />
                  </div>
                  <p className="mt-1 text-xs text-gray-400">Large files take a few minutes. Leave this page open.</p>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3.5 rounded-xl bg-primary-blue text-white font-black uppercase tracking-widest text-sm hover:opacity-90 disabled:opacity-50 transition"
              >
                {submitting ? (progress ? 'Uploading…' : 'Submitting…') : 'Submit request'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicForm;
